import React from 'react'
import { Outlet } from 'react-router-dom'

import { Box } from '@mui/material'

import { EncodingDeviceProvider, ScanRoutingProvider } from 'contexts'
import { useAskOwner, useAskShortcuts, useChecklistProgressToast, useSearchHotkey } from 'hooks'
import { useReferralsEnabled } from 'hooks/api/useFeatures'
import { ASK_PANEL_SLIDE_DURATION_MS, ASK_PANEL_SLIDE_EASE, useAskStore } from 'ask'

import { AskPanel, Sidebar } from 'components/navigation'
import { ProductTourProvider, ToursDialog } from 'components/onboarding'
import { KeyboardShortcutsDialog, ScanConnectModal, SearchOverlay, VeraSpaceBanner } from 'components'
import { AnnouncementModal } from 'components/feedback/AnnouncementModal/AnnouncementModal'
import { ReferABrandModal } from 'components/feedback/modals/ReferABrandModal/ReferABrandModal'
import { NewSupportRequestModal, useSupportIntake } from 'pages/support/NewSupportRequest'

export const Layout = () => {
  // The ⌘K palette and its shortcut live HERE rather than in App, so they exist for
  // exactly the authenticated, chrome-bearing pages. Mounted a level up they would also
  // be live on /sign-in and the scan-companion, where there is nothing to search and no
  // session to search it with.
  useSearchHotkey()
  // Binds the persisted Ask state to whoever is signed in, discarding it on a change of
  // user. Same reasoning as above for why it sits here: it needs a session to bind to.
  useAskOwner()
  // ⌘I toggles the panel, ⌘⇧⏎ starts a new chat — both live anywhere in the platform,
  // so they mount here rather than inside AskPanel itself (which does not exist while
  // the panel is closed).
  useAskShortcuts()
  // VRG-680 — the Refer & earn launch switch (`GET /features`), read once for the modal mount.
  const { enabled: referralsLive } = useReferralsEnabled()
  // VRG-227: the panel's fullscreen takeover collapses `main` to nothing so the panel
  // can fill the space up to the sidebar — read here because AskPanel has no path to
  // style a sibling it does not render.
  const askExpanded = useAskStore(s => s.open && s.expanded)
  // VRG-227 slide fix: `main`'s OWN width transition, so its resize settles alongside
  // the panel's open/close slide instead of snapping the instant the panel's reserved
  // width changes. See AskPanel's header docblock for the full reasoning and
  // `resizing`'s docblock (useAskStore) for why this is suppressed during a live drag.
  const askResizing = useAskStore(s => s.resizing)
  // VRG-226: /support/new is a modal over whatever page is already rendered, and
  // the URL is what opens it — no caller passes a prop or a callback. It hangs
  // off Layout rather than off <Routes> for two reasons: Layout is inside
  // ProtectedRoute (so an unauthenticated deep link redirects to sign-in instead
  // of flashing the form over a loader), and Layout renders for every page the
  // intake can be opened from, which is exactly when it should be available.
  const { isOpen: isSupportIntakeOpen } = useSupportIntake()
  // VRG-340: announces a setup step completing, from wherever the user finished it — every
  // step is completed on some page that is not Home, so without this the progress changes
  // while the only surface that shows it is off screen. Mounted here, once, because the
  // queries behind it are already running app-wide for the Sidebar's Home badge.
  useChecklistProgressToast()

  return (
    <EncodingDeviceProvider>
    {/* VRG-265 — scan routing sits ABOVE the pages so a scan session outlives the modal that
        opened it and the filter field it is aimed at (see ScanRoutingContext). Inside
        EncodingDeviceProvider because it relays desk-reader scans into that session. The pairing
        modal is rendered here, once, rather than by the provider: it is a component, and
        `contexts` must not import from `components` (that is a cycle). */}
    <ScanRoutingProvider>
    <ScanConnectModal />
    {/* Tours are mounted on the chromed shell only. Every anchor a tour points
        at (the sidebar, a page's controls) lives inside this layout, and an
        overlay on the sign-in page or the password-setup gate — both of which
        render outside Layout — would be a category error. Scoping it here makes
        that structural rather than a path list someone has to maintain. */}
    <ProductTourProvider>
    <Box>
      {/* overflowX hidden (VRG-227): the closed/opening Ask panel sits at
          `transform: translateX(100%)` — a full extra copy of its own width shifted
          past its box's right edge, which is otherwise the row's right edge too. Left
          unclipped, that would grow this row's (and so the page's) scrollable width by
          the panel's width whenever it is closed.

          `clip`, not `hidden` (VRG-643). `hidden` clips the paint but leaves the row a
          SCROLL CONTAINER with a hidden scroll range — measured 568px at 1280 wide with the
          panel closed — so anything that scrolls an element into view (focus, a
          `scrollIntoView` from a control near the right edge or inside the panel) slid the
          whole app sideways: one such call moved it 424px. `clip` paints the same and is
          not a scroll container, so there is nothing to slide. `hidden` stays as the
          fallback for engines without `clip` (pre-2022 Safari). */}
      <Box
        display="flex"
        sx={{ overflowX: 'hidden', '@supports (overflow: clip)': { overflowX: 'clip' } }}
      >
        <Sidebar />
        <Box
          component="main"
          sx={[
            {
              flexGrow: 1,
              flex: 1,
              // minWidth:0 lets this flex item shrink below its content's intrinsic
              // width; without it the main column grows past the viewport on narrow
              // screens (wide tables / the settings sub-nav), causing a horizontal
              // scroll into empty space. overflowX:hidden then clips wide children.
              minWidth: 0,
              // Compensate for the app text-zoom (useTextZoom sets body{zoom} + the
              // --text-zoom var). A plain 100vh renders at z×100vh under zoom, leaving
              // a black band; dividing by the factor makes it fill the viewport at any
              // zoom (falls back to 100vh when unset / at 100%).
              height: 'calc(100vh / var(--text-zoom, 1))',
              minHeight: 'calc(100vh / var(--text-zoom, 1))',
              bgcolor: 'background.default',
              overflowX: 'hidden',
              // Flex column so a page's table/content fills the remaining height and its
              // bottom lands at the `p: 6` padding — matching the left/right/top margins.
              display: 'flex',
              flexDirection: 'column',
              // Animates BOTH directions of the Ask panel's fullscreen takeover: the
              // page collapsing away when it opens, and growing back when it closes.
              //
              // `width` is ADDITIONAL to that (VRG-227 slide fix), for the panel's
              // ordinary open/close rather than its fullscreen takeover: AskPanel's own
              // reserved width now snaps (not transitions — see its docblock) the
              // moment `open` flips, so without this `main`'s resize would still be an
              // instant jump even though the panel's OWN slide is now smooth. Declaring
              // it here, matched to the same duration/curve, is what makes `main`
              // settle into its new width alongside the panel's slide rather than
              // snapping ahead of (open) or behind (close) it. Suppressed during a live
              // drag and during the fullscreen takeover — both already have their own,
              // untransitioned or differently-timed, way of changing this box's size,
              // and fighting either with a second transition would make the resize lag
              // the input driving it (see `askResizing`'s docblock, useAskStore).
              transition:
                askResizing || askExpanded
                  ? 'flex-grow 220ms ease, flex-basis 220ms ease'
                  : `flex-grow 220ms ease, flex-basis 220ms ease, width ${ASK_PANEL_SLIDE_DURATION_MS}ms ${ASK_PANEL_SLIDE_EASE}`,
              '@media (prefers-reduced-motion: reduce)': {
                transition: 'none',
              },
            },
            // VRG-227 fullscreen Ask panel: collapse the page to nothing so the panel
            // (a flex sibling below) grows to fill exactly the space this gives up —
            // no pixel math, the flexbox does it. `display:none` would skip the
            // transition entirely, so this collapses via flex instead and only clips
            // with overflow, which the transition can animate smoothly.
            askExpanded && {
              flexGrow: 0,
              flexBasis: 0,
              minWidth: 0,
              overflow: 'hidden',
            },
          ]}
        >
          {/* VERA-876: banner shown when Vera staff are operating inside a company's space */}
          <VeraSpaceBanner />
          <Box sx={{ p: 6, flexGrow: 1, overflow: 'auto' }}>
            <Outlet />
          </Box>
        </Box>
        {/* A flex sibling of `main`, NOT an overlay: opening it narrows the page instead
            of covering it, so everything behind stays visible and clickable. `main`
            already carries flexGrow + minWidth:0, so it shrinks correctly with no
            change here. */}
        <AskPanel />
      </Box>
      {/* Rendered through a portal by MUI's Modal, so its position in this tree only
          decides WHEN it exists, not where it paints. */}
      <SearchOverlay />

      {/* Mounted ONCE for the whole app, not per surface: its `?` listener is
          global and a second copy would open two stacked dialogs. Rendered here
          rather than inside the sidebar so it survives any future nav change —
          the shortcuts it documents are not the sidebar's. */}
      <KeyboardShortcutsDialog />

      {/* Same reasoning, same place: ONE mount, opened by `openToursDialog()` from
          the Help menu, the ⌘K footer, and the palette's "Browse all tours" row. It
          used to live inside the Help popover holding local `toursOpen` state, which
          is why the palette had no way to reach it at all. */}
      <ToursDialog />

      {/* VRG-655 — "Refer & earn". ONE mount, opened by `openReferABrandModal()` from the
          sidebar row, the Home checklist and the referrals page. Renders nothing until
          opened, and fetches the member's link only then (fetching it mints a code).
          VRG-680: not mounted at all while the launch switch is off, so a stray
          `openReferABrandModal()` has no listener and does nothing. */}
      {referralsLive && <ReferABrandModal />}

      {/* Portals to <body>, so where it sits in this tree only decides WHEN it
          renders, not where it appears. Mounted/unmounted by the URL so the form
          starts clean on every open. */}
      {isSupportIntakeOpen && <NewSupportRequestModal />}

      {/* VRG-221 — the once-per-session "What's new" dialog.
          Mounted HERE, once, for three reasons that all point at the same place:
            - it must be able to appear on ANY authenticated page, since the user's
              first navigation after a release could be anywhere;
            - it must NOT exist on /sign-in or the scan companion, which render outside
              Layout — announcing a feature to someone who is not signed in yet is
              addressed to nobody, and there is no user to record the dismissal against;
            - one instance only. A second copy would read the same feed and open a
              second stacked dialog, the same trap the KeyboardShortcutsDialog note
              above records.
          It sits INSIDE ProductTourProvider deliberately: an announcement's CTA can
          start a tour (tour_key), so the card needs `useProductTour` in scope. */}
      <AnnouncementModal />


    </Box>
    </ProductTourProvider>
    </ScanRoutingProvider>
    </EncodingDeviceProvider>
  )
}
